import React, { useState } from "react";
import { CheckCircle2, Lock } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSetupNewPasswordMutation } from "../../store/apis/auth.api";
import toast from "react-hot-toast";
import { Button, Input } from "../common";
import { ApiError } from "../../store/utils/apiError";
import AuthLayout from "./AuthLayout";

const SetupPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = searchParams.get("token") || "";
  const email = searchParams.get("email") || "";

  const [setupNewPassword, { isLoading }] = useSetupNewPasswordMutation();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");

  const handleSetupPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token || !email) {
      setError("Invalid setup link. Missing token or email.");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    const passwordPolicyRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z0-9]).{8,}$/;

    if (!passwordPolicyRegex.test(newPassword)) {
      setError(
        "Password must be at least 8 characters and include 1 uppercase, 1 lowercase, and 1 special character",
      );
      return;
    }

    setError("");
    try {
      await setupNewPassword({
        workEmail: email,
        token,
        newPassword: newPassword,
      }).unwrap();
      navigate("/login");
      toast.success(
        "Password set successfully! You can now login with your new password.",
      );
    } catch (error: unknown) {
      const err = error as ApiError;
      const rawMessage = err?.data?.message || err?.message || 'Failed to setup password';
      const errorMessage = Array.isArray(rawMessage) ? rawMessage.join(', ') : rawMessage;
      setError(errorMessage);
    }
  };

  return (
    <AuthLayout>
      <div className="animate-fade-in">
        <div className="mb-8">
          <h1 className="text-2xl font-extrabold text-slate-900 text-center tracking-tight">
            <span className="text-gradient">Setup</span>{" "}
            <span className="text-slate-900">Password</span>
          </h1>
          <p className="text-slate-500 text-center mt-2 text-sm font-medium">
            Create a secure password to activate your account.
          </p>
        </div>

        <form className="space-y-5 mb-1" onSubmit={handleSetupPassword}>
          <div>
            <label
              htmlFor="newPassword"
              className="block text-sm font-semibold text-slate-700 mb-2"
            >
              New Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg transition-all duration-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:ring-offset-1 hover:border-slate-300"
                placeholder="Enter new password"
                required
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-semibold text-slate-700 mb-2"
            >
              Confirm Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg transition-all duration-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:ring-offset-1 hover:border-slate-300"
                placeholder="Confirm new password"
                required
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm animate-fade-in text-center">
              {error}
            </div>
          )}

          <Button
            htmlType="submit"
            appearance="primary"
            size="large"
            loading={isLoading}
            disabled={
              isLoading || !newPassword.trim() || !confirmPassword.trim() || !token || !email
            }
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:!bg-blue-600 disabled:!text-white disabled:!border-blue-600"
          >
            <span className="font-bold tracking-wide">Complete Setup</span>
          </Button>
        </form>
      </div>
    </AuthLayout>
  );
};

export default SetupPassword;
